<template>
  <div class="question-list-evaluation">
    <ErrorMessage v-if="errorMessage">
      <p>{{ errorMessage }}</p>
    </ErrorMessage>
    <template v-else>
      <div class="evaluation-charts" :class="{ 'has-grade': grade }">
        <template v-if="grade">
          <h2 class="evaluation-chart-heading">
            {{
              $t('components.training.question-list-evaluation.grade-heading')
            }}
          </h2>
          <GradeGraphic class="evaluation-chart" :grade="grade" />
        </template>
        <h2 class="evaluation-chart-heading">
          {{
            $t(
              'components.training.question-list-evaluation.answers-chart-heading'
            )
          }}
        </h2>
        <DonutChartMultiValue
          class="evaluation-chart"
          :values="answerChartValues"
          :max="answerStatistics.answers.possible"
          :display-value="answerStatistics.answers.correct"
          :duration="chartTransitionDuration"
          >{{ $t('words.of') }}
          <AnimatedNumber
            :value="answerStatistics.answers.possible"
            :duration="chartTransitionDuration"
        /></DonutChartMultiValue>
        <h2 class="evaluation-chart-heading">
          {{
            $t(
              'components.training.question-list-evaluation.points-chart-heading'
            )
          }}
        </h2>
        <DonutChartMultiValue
          class="evaluation-chart"
          :values="pointsChartValues"
          :display-value="answerStatistics.points.achieved"
          :max="answerStatistics.points.possible"
          :duration="chartTransitionDuration"
          >{{ $t('words.of') }}
          <AnimatedNumber
            :value="answerStatistics.points.possible"
            :duration="chartTransitionDuration"
        /></DonutChartMultiValue>
      </div>
      <LessonScores v-if="lessonScores" :lesson-scores="lessonScores" />
      <ul class="question-list">
        <li
          v-for="(question, index) in questions"
          :key="question.id"
          class="question-list-item"
        >
          <Accordion :open="!isAnsweredCorrect(question)">
            <h2
              slot="header"
              class="question-heading"
              :class="getQuestionAnswerClassName(question)"
            >
              {{
                $t(
                  'components.training.question-list-evaluation.question-heading',
                  {
                    index: formatNumber(index + 1),
                  }
                )
              }}
              <FaIcon
                v-if="isAnswered(question)"
                class="question-heading-icon"
                :icon="isAnsweredCorrect(question) ? 'check' : 'times'"
              />
              <template v-else>
                ({{
                  $t(
                    'components.training.question-list-evaluation.unanswered-label'
                  )
                }})
              </template>
            </h2>
            <Question :data="question" :evaluate="true" :hide-header="true" />
          </Accordion>
        </li>
      </ul>
    </template>
  </div>
</template>

<script>
import Accordion from '@/components/ui/Accordion'
import AnimatedNumber from '@/components/ui/AnimatedNumber'
import DonutChartMultiValue from '@/components/charts/DonutChartMultiValue'
import ErrorMessage from '@/components/ui/ErrorMessage'
import FaIcon from '@/components/ui/FaIcon'
import GradeGraphic from '@/components/training/GradeGraphic'
import LessonScores from '@/components/training/LessonScores'
import Question from '@/components/Question'

import typesetMathMlMixin from '@/mixins/typeset-mathml'

import { QuestionStoreNamespacedTypes } from '@/store/questions'

import { formatNumber } from '@/utils/number'
import { getProperty, hasProperty } from '@/utils/object'
import { chartColors } from '@/colors'

export default {
  name: 'QuestionListEvaluation',
  mixins: [typesetMathMlMixin],
  components: {
    AnimatedNumber,
    Accordion,
    DonutChartMultiValue,
    ErrorMessage,
    FaIcon,
    GradeGraphic,
    LessonScores,
    Question,
  },
  props: {
    userAnswerModel: {
      type: Number,
      required: true,
    },
    sessionId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return { chartTransitionDuration: 500 }
  },
  computed: {
    answerChartValues() {
      return [
        {
          key: 'correct',
          color: chartColors.green,
          value: this.answerStatistics.answers.correct,
        },
        {
          key: 'wrong',
          color: chartColors.red,
          value: this.answerStatistics.answers.wrong,
        },
      ]
    },
    pointsChartValues() {
      return [
        {
          key: 'achieved',
          color: chartColors.green,
          value: this.answerStatistics.points.achieved,
        },
      ]
    },
    answerStatistics() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_ANSWER_STATISTICS
      ](this.userAnswerModel, this.sessionId)
    },
    errorMessage() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_RESULT_ERROR_MESSAGE
      ](this.userAnswerModel, this.sessionId)
    },
    grade() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_RESULT_GRADE
      ](this.userAnswerModel, this.sessionId)
    },
    lessonScores() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_RESULT_LESSON_SCORES
      ](this.userAnswerModel, this.sessionId)
    },
    questions() {
      return this.$store.getters[
        QuestionStoreNamespacedTypes.getters.GET_QUESTIONS
      ](this.userAnswerModel, this.sessionId)
    },
  },
  methods: {
    formatNumber,
    isAnswered(question) {
      return hasProperty(question, 'UserAnswer')
    },
    isAnsweredCorrect(question) {
      return getProperty(question, 'UserAnswer.correct', false)
    },
    getQuestionAnswerClassName(question) {
      if (!this.isAnswered(question)) {
        return 'unanswered'
      }

      if (this.isAnsweredCorrect(question)) {
        return 'correct'
      }

      return 'wrong'
    },
  },
}
</script>

<style lang="scss" scoped>
.evaluation-charts {
  display: grid;
  grid-template-columns: 50% 50%;
  grid-auto-flow: column;
  grid-template-rows: auto auto;
  margin: auto auto var(--content-holder-margin);
  max-width: 45em;
  text-align: center;

  &.has-grade {
    grid-template-columns: 1fr 1fr 1fr;
  }
}

.evaluation-chart-heading {
  margin: 0.5em 0;
}

.evaluation-chart {
  margin: auto;
}

.lesson-scores {
  margin: var(--content-padding-large);
}

.accordion {
  border: 1px solid $grayLighter;
}

.question-list-item {
  padding: var(--content-padding-large);

  // Reset global Fessor style
  background: none;
}

.question-heading {
  font-size: 1.15em;

  &.correct {
    color: $green;
  }

  &.wrong {
    color: $red;
  }

  &.unanswered {
    color: $yellow;
  }
}

.question-heading-icon {
  margin-left: 0.3em;
}
</style>
